import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PersonaAvatar from '../../components/PersonaAvatar'

interface CommunityUser {
  id: string; name: string; email?: string; role: string; status?: string;
  is_human?: boolean; is_persona?: boolean; api_key?: string; created_at?: string
}

export default function CommunityUserDetailPage() {
  const { communityId, userId } = useParams<{ communityId: string; userId: string }>()
  const queryClient = useQueryClient()
  const [newPassword, setNewPassword] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  const { data: users } = useQuery({
    queryKey: ['communityUsers', communityId],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${communityId}/users`)
      if (!res.ok) return { users: [] }
      return res.json() as Promise<{ users: CommunityUser[] }>
    },
    enabled: !!communityId,
  })

  const user = users?.users?.find(u => u.id === userId)

  const updateMut = useMutation({
    mutationFn: async (patch: Record<string, string>) => {
      const res = await fetch(`/api/communities/${communityId}/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      if (!res.ok) throw new Error('Failed')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['communityUsers', communityId] }),
  })

  const resetPwMut = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/communities/${communityId}/users/${userId}/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      })
      if (!res.ok) throw new Error('Failed')
      return res.json()
    },
    onSuccess: () => { setNewPassword(''); setMessage('Password reset') },
  })

  if (!user) return <div className="p-6 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <Link to="/communities" className="text-xs mb-4 inline-block no-underline" style={{ color: 'var(--text-muted)' }}>← Communities</Link>

      <div className="flex items-center gap-4 mb-6">
        <PersonaAvatar name={user.name} size={48} />
        <div>
          <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{user.name}</h1>
          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{user.email || 'No email'}</div>
        </div>
      </div>

      {/* Details */}
      <section className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Details</h2>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <span style={{ color: 'var(--text-muted)' }}>ID</span><span style={{ color: 'var(--text-primary)' }}>{user.id}</span>
          <span style={{ color: 'var(--text-muted)' }}>Role</span>
          <select
            value={user.role}
            onChange={e => updateMut.mutate({ role: e.target.value })}
            className="px-2 py-0.5 rounded text-xs outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          >
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </select>
          <span style={{ color: 'var(--text-muted)' }}>Status</span>
          <select
            value={user.status || 'active'}
            onChange={e => updateMut.mutate({ status: e.target.value })}
            className="px-2 py-0.5 rounded text-xs outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="revoked">Revoked</option>
          </select>
          <span style={{ color: 'var(--text-muted)' }}>Type</span>
          <span style={{ color: 'var(--text-primary)' }}>
            {user.is_persona ? 'Persona' : user.is_human ? 'Human' : 'User'}
          </span>
          {user.api_key && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>API Key</span>
              <span className="font-mono truncate" style={{ color: 'var(--text-primary)' }}>{user.api_key}</span>
            </>
          )}
        </div>
      </section>

      {/* Password reset */}
      <section className="p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Reset Password</h2>
        <div className="flex gap-2">
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="New password (min 6)"
            className="flex-1 px-3 py-1.5 rounded text-xs outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          <button
            onClick={() => resetPwMut.mutate()}
            disabled={newPassword.length < 6}
            className="text-xs px-3 py-1.5 rounded disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            Reset
          </button>
        </div>
        {message && <p className="text-xs mt-2" style={{ color: 'var(--success)' }}>{message}</p>}
      </section>
    </div>
  )
}
